import ngeohash from 'ngeohash';

export interface GeohashBounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

export interface GeohashData {
  hash: string;
  bounds: GeohashBounds;
}

/**
 * Get the bounding box for a geohash
 */
export function getGeohashBounds(hash: string): GeohashBounds {
  const [minLat, minLng, maxLat, maxLng] = ngeohash.decode_bbox(hash);
  return { minLat, maxLat, minLng, maxLng };
}

/**
 * Calculate geohashes that cover a given bounding box at a specific precision level
 */
export function getGeohashesInBounds(
  bounds: GeohashBounds,
  precision: number
): GeohashData[] {
  const hashes = ngeohash.bboxes(
    bounds.minLat,
    bounds.minLng,
    bounds.maxLat,
    bounds.maxLng,
    precision
  );

  return hashes.map((hash) => ({
    hash,
    bounds: getGeohashBounds(hash),
  }));
}

/**
 * Check if a point is inside a polygon using ray casting algorithm
 */
export function pointInPolygon(
  point: [number, number],
  polygon: [number, number][]
): boolean {
  const [x, y] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Check if a geohash intersects with a polygon
 * A geohash intersects if any of its corners are inside the polygon,
 * or if the polygon has any vertices inside the geohash
 */
export function geohashIntersectsPolygon(
  geohash: GeohashData,
  polygon: [number, number][]
): boolean {
  const { minLat, maxLat, minLng, maxLng } = geohash.bounds;

  // Check if any corner of the geohash is inside the polygon
  const corners: [number, number][] = [
    [minLng, minLat],
    [maxLng, minLat],
    [maxLng, maxLat],
    [minLng, maxLat],
  ];

  for (const corner of corners) {
    if (pointInPolygon(corner, polygon)) {
      return true;
    }
  }

  // Check if the center of the geohash is inside the polygon
  const centerLng = (minLng + maxLng) / 2;
  const centerLat = (minLat + maxLat) / 2;
  if (pointInPolygon([centerLng, centerLat], polygon)) {
    return true;
  }

  // Check if any polygon vertex is inside the geohash
  for (const [lng, lat] of polygon) {
    if (lng >= minLng && lng <= maxLng && lat >= minLat && lat <= maxLat) {
      return true;
    }
  }

  return false;
}

/**
 * Get all geohashes that cover a polygon at a specific precision
 */
export function getGeohashesForPolygon(
  polygon: [number, number][],
  precision: number
): GeohashData[] {
  // Calculate bounding box of the polygon
  const lngs = polygon.map(([lng]) => lng);
  const lats = polygon.map(([, lat]) => lat);

  const bounds: GeohashBounds = {
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
    minLng: Math.min(...lngs),
    maxLng: Math.max(...lngs),
  };

  // Get all geohashes in the bounding box
  const allGeohashes = getGeohashesInBounds(bounds, precision);

  // Filter to only include geohashes that intersect with the polygon
  return allGeohashes.filter((gh) => geohashIntersectsPolygon(gh, polygon));
}

/**
 * Generate a color based on geohash string for visualization
 */
export function getGeohashColor(hash: string): string {
  let hashNum = 0;
  for (let i = 0; i < hash.length; i++) {
    hashNum = hash.charCodeAt(i) + ((hashNum << 5) - hashNum);
  }

  const hue = Math.abs(hashNum % 360);
  return `hsl(${hue}, 70%, 50%)`;
}

/**
 * Precision level descriptions
 */
export const PRECISION_DESCRIPTIONS: Record<number, string> = {
  1: '~5,000 km × 5,000 km',
  2: '~1,250 km × 625 km',
  3: '~156 km × 156 km',
  4: '~39 km × 19.5 km',
  5: '~4.9 km × 4.9 km',
  6: '~1.2 km × 610 m',
  7: '~153 m × 153 m',
  8: '~38 m × 19 m',
  9: '~4.8 m × 4.8 m',
  10: '~1.2 m × 60 cm',
  11: '~15 cm × 15 cm',
  12: '~3.7 cm × 1.9 cm',
};

/**
 * Calculate the distance between two points using the Haversine formula
 * @returns distance in meters
 */
export function haversineDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Check if a geohash is within a given radius of a point
 * Uses the center of the geohash for distance calculation
 */
export function isGeohashWithinRadius(
  geohash: GeohashData,
  centerLat: number,
  centerLng: number,
  radiusMeters: number
): boolean {
  const { minLat, maxLat, minLng, maxLng } = geohash.bounds;
  const ghCenterLat = (minLat + maxLat) / 2;
  const ghCenterLng = (minLng + maxLng) / 2;

  const distance = haversineDistance(centerLat, centerLng, ghCenterLat, ghCenterLng);
  return distance <= radiusMeters;
}

/**
 * Calculate bounding box from a center point and radius
 * @param lat - center latitude
 * @param lng - center longitude
 * @param radiusMeters - radius in meters
 */
export function getBoundingBoxFromRadius(
  lat: number,
  lng: number,
  radiusMeters: number
): GeohashBounds {
  // Earth's radius in meters
  const R = 6371000;

  // Angular distance in radians
  const angularDistance = radiusMeters / R;

  // Calculate latitude bounds
  const latRad = (lat * Math.PI) / 180;
  const minLat = lat - (angularDistance * 180) / Math.PI;
  const maxLat = lat + (angularDistance * 180) / Math.PI;

  // Calculate longitude bounds (accounting for latitude)
  const deltaLng = Math.asin(Math.sin(angularDistance) / Math.cos(latRad));
  const minLng = lng - (deltaLng * 180) / Math.PI;
  const maxLng = lng + (deltaLng * 180) / Math.PI;

  return {
    minLat: Math.max(-90, minLat),
    maxLat: Math.min(90, maxLat),
    minLng: Math.max(-180, minLng),
    maxLng: Math.min(180, maxLng),
  };
}

/**
 * Get all geohashes near a point within a given radius
 */
export function getGeohashesNearPoint(
  lat: number,
  lng: number,
  radiusMeters: number,
  precision: number
): GeohashData[] {
  // Get bounding box that encompasses the radius
  const bounds = getBoundingBoxFromRadius(lat, lng, radiusMeters);

  // Get all geohashes in the bounding box
  const allGeohashes = getGeohashesInBounds(bounds, precision);

  // Filter to only include geohashes whose centers are within the radius
  return allGeohashes.filter((gh) =>
    isGeohashWithinRadius(gh, lat, lng, radiusMeters)
  );
}

/**
 * Encode a lat/lng coordinate to a geohash
 */
export function encodeGeohash(lat: number, lng: number, precision: number): string {
  return ngeohash.encode(lat, lng, precision);
}

/**
 * Decode a geohash to lat/lng coordinates
 */
export function decodeGeohash(hash: string): { latitude: number; longitude: number } {
  return ngeohash.decode(hash);
}
